import { useState } from "react";
import {
    CheckCircle2Icon,
    InfoIcon,
    OctagonXIcon,
    TriangleAlertIcon,
    type LucideIcon,
} from "lucide-react";

import Layout from "../../components/Layout";
import ChipRadioGroup from "../../components/ChipRadioGroup";
import ChipRange from "../../components/ChipRange";
import ChipSelect from "../../components/ChipSelect";
import { FormCheckbox } from "@/components/forms/FormCheckbox";
import { FormInput } from "@/components/forms/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/forms/FormRadio";
import { Heading } from "@/components/typography/heading";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Mix chrome reference: header cyan-200 (Layout), CTA cyan-300 / foreground,
 * soft controls on --primary (cyan-500). See Color system in index.css.
 */

type AlertVariant = "default" | "destructive" | "success" | "warning";

const PREVIEW_ALERTS: {
    variant: AlertVariant;
    icon: LucideIcon;
    title: string;
    body: string;
}[] = [
    {
        variant: "default",
        icon: InfoIcon,
        title: "Heads up",
        body: "New creators matching your filters were added this week.",
    },
    {
        variant: "success",
        icon: CheckCircle2Icon,
        title: "Changes saved",
        body: "Talent profile updates are live for this campaign.",
    },
    {
        variant: "warning",
        icon: TriangleAlertIcon,
        title: "Subscription expiring",
        body: "Your plan renews in 3 days — update billing to avoid interruption.",
    },
    {
        variant: "destructive",
        icon: OctagonXIcon,
        title: "Payment failed",
        body: "We couldn’t charge the card on file. Retry or update payment.",
    },
];

/** Soft semantic Badge variants (default = cyan soft). */
const SOFT_BADGES: {
    name: string
    variant: "default" | "success" | "warning" | "orange" | "destructive" | "blue" | "purple"
}[] = [
    { name: "cyan", variant: "default" },
    { name: "success", variant: "success" },
    { name: "warning", variant: "warning" },
    { name: "orange", variant: "orange" },
    { name: "destructive", variant: "destructive" },
    { name: "blue", variant: "blue" },
    { name: "purple", variant: "purple" },
];

export default function Preview() {
    const [name, setName] = useState("Alex Rivera");
    const [notify, setNotify] = useState(true);
    const [invoiceNotes, setInvoiceNotes] = useState(false);
    const [platform, setPlatform] = useState("TikTok");
    const [chipMulti, setChipMulti] = useState<string[]>(["Dance"]);
    const [chipSingle, setChipSingle] = useState("Song");
    const [chipRange, setChipRange] = useState([10_000, 500_000]);
    const [chipRadio, setChipRadio] = useState<string | undefined>("any");

    return (
        <Layout>
            <div className="container space-y-6 p-6">
                <div>
                    <Heading type="h1" className="mb-2">
                        UI preview
                    </Heading>
                    <p className="max-w-2xl text-sm text-muted-foreground">
                        Mix chrome — header{" "}
                        <code className="text-sm">cyan-200</code>, primary button{" "}
                        <code className="text-sm">cyan-300</code> / foreground, soft
                        checkbox/radio/slider on{" "}
                        <code className="text-sm">--primary</code> (cyan-500).
                    </p>
                </div>

                {/* Row: 50/50 — controls + form */}
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                    <div className="space-y-6">
                        <section className="space-y-3">
                            <Heading type="h2">Primary button</Heading>
                            {/* Row: Mix primary + contrast cancel */}
                            <div className="flex flex-wrap gap-3">
                                <Button type="button">Save changes</Button>
                                <Button type="button" variant="contrast">
                                    Cancel
                                </Button>
                            </div>
                        </section>

                        <section className="space-y-3">
                            <Heading type="h2">Soft semantic badges</Heading>
                            <div className="flex flex-wrap gap-2">
                                {SOFT_BADGES.map(({ name: badgeName, variant }) => (
                                    <Badge
                                        key={badgeName}
                                        variant={variant}
                                        className="capitalize"
                                    >
                                        {badgeName}
                                    </Badge>
                                ))}
                                <Badge variant="secondary">Secondary</Badge>
                                <Badge variant="outline">Outline</Badge>
                            </div>
                        </section>

                        <section className="space-y-3">
                            <Heading type="h2">Chips</Heading>
                            {/* Row: distinct Chip* surfaces */}
                            <div className="flex flex-wrap items-center gap-2">
                                <ChipSelect
                                    label={
                                        chipMulti.length
                                            ? `Tags · ${chipMulti.length}`
                                            : "Tags"
                                    }
                                    active={chipMulti.length > 0}
                                    options={["Dance", "Comedy", "Lifestyle"]}
                                    value={chipMulti}
                                    onChange={(next) =>
                                        setChipMulti(
                                            Array.isArray(next) ? next : [next]
                                        )
                                    }
                                />
                                <ChipSelect
                                    label={`Type · ${chipSingle}`}
                                    active
                                    singleSelect
                                    options={["Song", "Brand"]}
                                    value={chipSingle}
                                    onChange={(next) =>
                                        setChipSingle(
                                            typeof next === "string"
                                                ? next
                                                : next[0] ?? "Song"
                                        )
                                    }
                                />
                                <ChipRange
                                    label={
                                        chipRange[0] === 10_000 &&
                                        chipRange[1] === 500_000
                                            ? "Followers"
                                            : `${chipRange[0].toLocaleString()}–${chipRange[1].toLocaleString()}`
                                    }
                                    min={10_000}
                                    max={500_000}
                                    step={1_000}
                                    value={chipRange}
                                    onChange={(next) => {
                                        if (next)
                                            setChipRange([
                                                next.lower,
                                                next.upper,
                                            ]);
                                    }}
                                />
                            </div>
                            <ChipRadioGroup
                                value={chipRadio}
                                onChange={setChipRadio}
                                options={[
                                    { label: "Any", value: "any" },
                                    { label: "Exclusive", value: "exclusive" },
                                    { label: "Open", value: "open" },
                                ]}
                            />
                        </section>
                    </div>

                    <section className="space-y-4">
                        <Heading type="h2">Form fields</Heading>

                        <FormInput
                            label="Display name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />

                        <FormCheckbox
                            label="Email me when this creator is booked"
                            checked={notify}
                            onChange={setNotify}
                        />

                        <FormCheckbox
                            label="Requires invoice notes"
                            checked={invoiceNotes}
                            onChange={setInvoiceNotes}
                        />

                        <FormRadioGroup
                            label="Campaign platform"
                            value={platform}
                            onChange={(next) => {
                                if (next) setPlatform(next);
                            }}
                        >
                            <FormRadio value="TikTok" label="TikTok" />
                            <FormRadio value="Instagram" label="Instagram" />
                            <FormRadio value="YouTube" label="YouTube" />
                        </FormRadioGroup>

                        {/* Row: form actions */}
                        <div className="flex flex-wrap gap-3">
                            <Button type="button">Save</Button>
                            <Button type="button" variant="outline">
                                Cancel
                            </Button>
                        </div>
                    </section>
                </div>

                <section className="space-y-3">
                    <Heading type="h2">Alerts</Heading>
                    {/* Row: all semantic alert surfaces */}
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                        {PREVIEW_ALERTS.map(
                            ({ variant, icon: Icon, title, body }) => (
                                <Alert key={variant} variant={variant}>
                                    <Icon />
                                    <AlertTitle>{title}</AlertTitle>
                                    <AlertDescription>{body}</AlertDescription>
                                </Alert>
                            )
                        )}
                    </div>
                </section>
            </div>
        </Layout>
    );
}
